import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {presenceApiClient} from '#/lib/api/presence/client'
import {useProfileFollowsQuery} from '#/state/queries/profile-follows'
import {useSession} from '#/state/session'

export type PresenceStatus = 'online' | 'idle' | 'offline'

export type OnlineUser = {
  did: string
  handle: string
  displayName: string
  avatar: string | undefined
  status: PresenceStatus
  privateRoomSlug?: string
}

const HEARTBEAT_INTERVAL_MS = 30_000
const STATUS_POLL_INTERVAL_MS = 30_000

/**
 * Sends heartbeats to the presence API while the user is active.
 * Runs at app level to maintain online status.
 */
export function usePresenceHeartbeat() {
  const {currentAccount} = useSession()

  useEffect(() => {
    if (!currentAccount?.did) return

    const did = currentAccount.did

    // Send initial heartbeat
    presenceApiClient.sendHeartbeat(did).catch(() => {})

    const interval = setInterval(() => {
      presenceApiClient.sendHeartbeat(did).catch(() => {})
    }, HEARTBEAT_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [currentAccount?.did])
}

/**
 * Presence hook that queries the real presence API for online status
 * of the user's mutual follows.
 */
export function useOnlineUsers(): {
  users: OnlineUser[]
  isLoading: boolean
} {
  const {currentAccount} = useSession()
  const {
    data: followsData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isFollowsLoading,
  } = useProfileFollowsQuery(currentAccount?.did)
  const [statuses, setStatuses] = useState<
    Record<string, PresenceStatus>
  >({})
  const [privateRooms, setPrivateRooms] = useState<
    Record<string, string>
  >({})
  const [isLoading, setIsLoading] = useState(true)
  const wsRefs = useRef<WebSocket[]>([])

  // Auto-fetch all pages of follows to get complete mutual list
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Extract mutual follows (people we follow who also follow us back)
  const partners = useMemo(() => {
    if (!followsData?.pages) return []

    const mutuals: {
      did: string
      handle: string
      displayName: string
      avatar?: string
    }[] = []

    for (const page of followsData.pages) {
      for (const profile of page.follows) {
        if (profile.viewer?.followedBy) {
          mutuals.push({
            did: profile.did,
            handle: profile.handle,
            displayName: profile.displayName || profile.handle,
            avatar: profile.avatar,
          })
        }
      }
    }

    return mutuals
  }, [followsData])

  // Poll presence statuses
  const fetchStatuses = useCallback(async () => {
    if (partners.length === 0) {
      setStatuses({})
      setIsLoading(false)
      return
    }

    try {
      const dids = partners.map(p => p.did)
      const result = await presenceApiClient.getStatuses(dids)

      const newStatuses: Record<string, PresenceStatus> = {}
      const newPrivateRooms: Record<string, string> = {}
      for (const [did, presence] of Object.entries(result.statuses)) {
        newStatuses[did] = presence.status
        if (presence.privateRoomSlug) {
          newPrivateRooms[did] = presence.privateRoomSlug
        }
      }
      setStatuses(newStatuses)
      setPrivateRooms(newPrivateRooms)
    } catch {
      // On error, keep previous statuses
    } finally {
      setIsLoading(false)
    }
  }, [partners])

  // Initial fetch + polling interval
  useEffect(() => {
    fetchStatuses()

    const interval = setInterval(fetchStatuses, STATUS_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchStatuses])

  // WebSocket subscriptions for real-time updates
  useEffect(() => {
    // Clean up previous WebSocket connections
    for (const ws of wsRefs.current) {
      ws.close()
    }
    wsRefs.current = []

    if (partners.length === 0) return

    for (const partner of partners) {
      try {
        const wsUrl = presenceApiClient.getWebSocketUrl(partner.did)
        const ws = new WebSocket(wsUrl)

        ws.onmessage = event => {
          try {
            const data = JSON.parse(event.data) as {
              type: string
              did: string
              status?: PresenceStatus
              roomSlug?: string
            }
            if (data.type === 'status-change' && data.status) {
              setStatuses(prev => ({
                ...prev,
                [data.did]: data.status!,
              }))
            } else if (data.type === 'private-room') {
              setPrivateRooms(prev => {
                if (data.roomSlug) {
                  return {...prev, [data.did]: data.roomSlug}
                }
                const next = {...prev}
                delete next[data.did]
                return next
              })
            }
          } catch {
            // Ignore malformed messages
          }
        }

        ws.onerror = () => {
          // Silently handle WebSocket errors — polling is the fallback
        }

        wsRefs.current.push(ws)
      } catch {
        // If WebSocket creation fails, polling still works
      }
    }

    return () => {
      for (const ws of wsRefs.current) {
        ws.close()
      }
      wsRefs.current = []
    }
  }, [partners])

  // Build the final user list sorted by status
  const users = useMemo(() => {
    return partners
      .map(partner => ({
        did: partner.did,
        handle: partner.handle,
        displayName: partner.displayName,
        avatar: partner.avatar,
        status: statuses[partner.did] || ('offline' as PresenceStatus),
        privateRoomSlug: privateRooms[partner.did],
      }))
      .sort((a, b) => {
        const order = {online: 0, idle: 1, offline: 2}
        return order[a.status] - order[b.status]
      })
  }, [partners, statuses, privateRooms])

  return {users, isLoading: isLoading || isFollowsLoading}
}
